/**
 * Inline file import widget for KA codes — same functionality as FileImportModal
 * but rendered inline (no modal wrapper). Used in task creation form.
 */
import { useState, useCallback, useRef, type ChangeEvent } from 'react'
import { Upload, ChevronRight, ChevronLeft, CheckCircle, FileSpreadsheet, FileText, Package, Layers } from 'lucide-react'
import { AppIcon } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { useFilePreview } from '@/hooks/useFileParser'
import type { SeparatorType, FilePreviewResult, ParseConfig } from '@/types'
import { SEPARATOR_LABELS, DEFAULT_PARSE_CONFIG } from '@/types'

export interface FileImportInlineData {
  fileBoxes: File
  filePallets?: File
  config: ParseConfig
}

type Step = 'file' | 'preview'

interface FileImportInlineProps {
  needsTwoFiles?: boolean
  codeType: string
  onChange: (data: FileImportInlineData | null) => void
}

const STORAGE_KEY_PREFIX = 'truemark_file_import_'

function loadSavedConfig(codeType: string): Partial<ParseConfig> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + codeType)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveConfig(codeType: string, config: ParseConfig) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + codeType, JSON.stringify(config))
  } catch { /* ignore */ }
}

export function FileImportInline({
  needsTwoFiles = false,
  codeType,
  onChange,
}: FileImportInlineProps) {
  const previewMutation = useFilePreview()

  const [step, setStep] = useState<Step>('file')
  const [fileBoxes, setFileBoxes] = useState<File | null>(null)
  const [filePallets, setFilePallets] = useState<File | null>(null)
  const [fileMode, setFileMode] = useState<'two_files' | 'single_file'>('two_files')
  const [preview, setPreview] = useState<FilePreviewResult | null>(null)
  const [previewPallets, setPreviewPallets] = useState<FilePreviewResult | null>(null)
  const [previewTab, setPreviewTab] = useState<'boxes' | 'pallets'>('boxes')
  const [config, setConfig] = useState<ParseConfig>(() => ({
    ...DEFAULT_PARSE_CONFIG,
    ...loadSavedConfig(codeType),
  }))
  const [columnTarget, setColumnTarget] = useState<'boxes' | 'pallets'>('boxes')
  const [confirmed, setConfirmed] = useState(false)

  const isDualColumnMode = needsTwoFiles && fileMode === 'single_file'

  const fileBoxesInputRef = useRef<HTMLInputElement>(null)
  const filePalletsInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>, which: 'boxes' | 'pallets') => {
    const file = e.target.files?.[0] ?? null
    if (which === 'boxes') {
      setFileBoxes(file)
      setConfig(prev => ({ ...prev, column_index: 0, start_row: 0, column_index_pallets: undefined }))
      setPreview(null)
    } else {
      setFilePallets(file)
      setPreviewPallets(null)
    }
    setConfirmed(false)
    onChange(null)
  }, [onChange])

  const handlePreview = useCallback(async () => {
    if (!fileBoxes) return
    const result = await previewMutation.mutateAsync({
      file: fileBoxes,
      separator: config.separator,
      encoding: config.encoding,
    })
    setPreview(result)
    if (config.separator === 'auto' && result.detected_separator) {
      setConfig(prev => ({ ...prev, separator: result.detected_separator as SeparatorType }))
    }
    if (needsTwoFiles && fileMode === 'two_files' && filePallets) {
      const palletResult = await previewMutation.mutateAsync({
        file: filePallets,
        separator: result.detected_separator as SeparatorType ?? config.separator,
        encoding: config.encoding,
      })
      setPreviewPallets(palletResult)
    } else {
      setPreviewPallets(null)
    }
    setPreviewTab('boxes')
    setStep('preview')
  }, [fileBoxes, filePallets, config.separator, config.encoding, previewMutation, needsTwoFiles, fileMode])

  const handleStartRowClick = useCallback((rowIndex: number) => {
    setConfig(prev => ({ ...prev, start_row: rowIndex }))
    setConfirmed(false)
    onChange(null)
  }, [onChange])

  const handleColumnClick = useCallback((colIndex: number) => {
    if (isDualColumnMode) {
      if (columnTarget === 'boxes') {
        setConfig(prev => ({
          ...prev, column_index: colIndex,
          column_index_pallets: prev.column_index_pallets === colIndex ? undefined : prev.column_index_pallets,
        }))
      } else {
        setConfig(prev => ({
          ...prev, column_index_pallets: colIndex,
          column_index: prev.column_index === colIndex ? 0 : prev.column_index,
        }))
      }
    } else {
      setConfig(prev => ({ ...prev, column_index: colIndex }))
    }
    setConfirmed(false)
    onChange(null)
  }, [isDualColumnMode, columnTarget, onChange])

  const handleSeparatorChange = useCallback(async (sep: SeparatorType) => {
    setConfig(prev => ({ ...prev, separator: sep }))
    if (fileBoxes) {
      const result = await previewMutation.mutateAsync({
        file: fileBoxes, separator: sep, encoding: config.encoding,
      })
      setPreview(result)
    }
    if (needsTwoFiles && fileMode === 'two_files' && filePallets) {
      const palletResult = await previewMutation.mutateAsync({
        file: filePallets, separator: sep, encoding: config.encoding,
      })
      setPreviewPallets(palletResult)
    }
  }, [fileBoxes, filePallets, config.encoding, previewMutation, needsTwoFiles, fileMode])

  const handleConfirm = useCallback(() => {
    if (!fileBoxes) return
    const finalConfig: ParseConfig = {
      ...config,
      file_mode: needsTwoFiles ? fileMode : undefined,
    }
    saveConfig(codeType, finalConfig)
    setConfirmed(true)
    onChange({
      fileBoxes,
      filePallets: filePallets ?? undefined,
      config: finalConfig,
    })
  }, [fileBoxes, filePallets, config, fileMode, needsTwoFiles, codeType, onChange])

  const canPreview = !!fileBoxes
  const canConfirm = !!fileBoxes
    && (!needsTwoFiles || fileMode === 'single_file' || !!filePallets)
    && (!isDualColumnMode || config.column_index_pallets !== undefined)

  const activePreview = previewTab === 'pallets' && previewPallets ? previewPallets : preview

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <StepBadge label="Файл" active={step === 'file'} done={step !== 'file'} num={1} />
        <ChevronRight className="w-4 h-4 text-gray-300" />
        <StepBadge label="Предпросмотр" active={step === 'preview' && !confirmed} done={confirmed} num={2} />
        <ChevronRight className="w-4 h-4 text-gray-300" />
        <StepBadge label="Подтверждено" active={false} done={confirmed} num={3} />
        {confirmed && (
          <>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <span className="flex items-center gap-1 text-green-600 font-medium text-xs">
              <CheckCircle className="w-4 h-4" /> Готово
            </span>
          </>
        )}
      </div>

      {step === 'file' && (
        <div className="space-y-3">
          {needsTwoFiles && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Режим файлов</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="kaFileMode" value="two_files"
                    checked={fileMode === 'two_files'} onChange={() => setFileMode('two_files')} className="text-blue-600" />
                  <span className="text-xs">Два файла (упаковки + паллеты)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="kaFileMode" value="single_file"
                    checked={fileMode === 'single_file'} onChange={() => setFileMode('single_file')} className="text-blue-600" />
                  <span className="text-xs">Один файл (все коды)</span>
                </label>
              </div>
            </div>
          )}

          <FileDropZone label={needsTwoFiles && fileMode === 'two_files' ? 'Файл с кодами упаковок' : 'Файл с КА кодами'}
            file={fileBoxes} inputRef={fileBoxesInputRef} onSelect={(e) => handleFileSelect(e, 'boxes')} />

          {needsTwoFiles && fileMode === 'two_files' && (
            <FileDropZone label="Файл с кодами паллет"
              file={filePallets} inputRef={filePalletsInputRef} onSelect={(e) => handleFileSelect(e, 'pallets')} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Разделитель</label>
              <select className="w-full border rounded-md px-2 py-1.5 text-xs"
                value={config.separator}
                onChange={(e) => setConfig(prev => ({ ...prev, separator: e.target.value as SeparatorType }))}>
                {(Object.entries(SEPARATOR_LABELS) as [SeparatorType, string][]).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Кодировка</label>
              <select className="w-full border rounded-md px-2 py-1.5 text-xs"
                value={config.encoding}
                onChange={(e) => setConfig(prev => ({ ...prev, encoding: e.target.value }))}>
                <option value="auto">Автоопределение</option>
                <option value="utf-8">UTF-8</option>
                <option value="cp1251">Windows-1251</option>
              </select>
            </div>
          </div>

          <Button type="button" size="sm" onClick={handlePreview} disabled={!canPreview} isLoading={previewMutation.isPending}>
            Предпросмотр <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-3">
          {previewPallets && (
            <div className="flex gap-1 border-b">
              <button type="button" onClick={() => setPreviewTab('boxes')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  previewTab === 'boxes' ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <Package className="w-3 h-3 inline mr-1" />Файл упаковок
              </button>
              <button type="button" onClick={() => setPreviewTab('pallets')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  previewTab === 'pallets' ? 'border-orange-500 text-orange-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <Layers className="w-3 h-3 inline mr-1" />Файл паллет
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>Строк: <strong>{activePreview!.total_lines}</strong></span>
            <span>Колонок: <strong>{activePreview!.column_count}</strong></span>
            <span>Кодировка: <strong>{activePreview!.encoding_detected}</strong></span>
          </div>

          {!activePreview!.is_excel && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Разделитель</label>
              <select className="w-40 border rounded-md px-2 py-1.5 text-xs"
                value={config.separator}
                onChange={(e) => handleSeparatorChange(e.target.value as SeparatorType)}
                disabled={previewMutation.isPending}>
                {(Object.entries(SEPARATOR_LABELS) as [SeparatorType, string][]).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          )}

          <div className="text-xs text-gray-500 bg-blue-50 rounded-md px-3 py-2">
            Кликните на <strong>строку</strong> чтобы указать начало данных.
            {isDualColumnMode ? (
              <> Выберите целевую колонку ниже, затем кликните на <strong>заголовок</strong>.</>
            ) : (
              <> Кликните на <strong>заголовок колонки</strong> чтобы выбрать колонку с кодами.</>
            )}
          </div>

          {isDualColumnMode && (
            <div className="flex gap-2">
              <button type="button" onClick={() => setColumnTarget('boxes')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  columnTarget === 'boxes' ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}>
                <Package className="w-3 h-3" /> Упаковки
                {config.column_index !== undefined && (
                  <span className="ml-1 px-1 py-0.5 bg-blue-200 text-blue-900 rounded text-xs">Кол. {config.column_index + 1}</span>
                )}
              </button>
              <button type="button" onClick={() => setColumnTarget('pallets')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  columnTarget === 'pallets' ? 'bg-orange-100 border-orange-400 text-orange-800' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}>
                <Layers className="w-3 h-3" /> Паллеты
                {config.column_index_pallets !== undefined && (
                  <span className="ml-1 px-1 py-0.5 bg-orange-200 text-orange-900 rounded text-xs">Кол. {config.column_index_pallets + 1}</span>
                )}
              </button>
            </div>
          )}

          <div className="overflow-auto max-h-[250px] border rounded-md">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left text-gray-400 w-10">#</th>
                  {Array.from({ length: activePreview!.column_count }, (_, i) => {
                    const isBoxCol = config.column_index === i
                    const isPalletCol = isDualColumnMode && config.column_index_pallets === i
                    return (
                      <th key={i}
                        className={`px-2 py-1.5 text-left cursor-pointer hover:bg-blue-100 transition-colors ${
                          isBoxCol ? 'bg-blue-200 text-blue-800 font-bold' : isPalletCol ? 'bg-orange-200 text-orange-800 font-bold' : 'text-gray-500'
                        }`}
                        onClick={() => handleColumnClick(i)}>
                        Кол. {i + 1}{isBoxCol && (isDualColumnMode ? <span className="ml-1 inline-flex align-middle"><AppIcon name="box" className="h-3 w-3" /></span> : ' ✓')}{isPalletCol && <span className="ml-1 inline-flex align-middle"><AppIcon name="pallet" className="h-3 w-3" /></span>}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activePreview!.rows.map((row, rowIdx) => {
                  const isBeforeStart = rowIdx < config.start_row
                  const isStartRow = rowIdx === config.start_row
                  return (
                    <tr key={rowIdx}
                      className={`cursor-pointer transition-colors ${
                        isBeforeStart ? 'bg-gray-50 text-gray-300 line-through' : isStartRow ? 'bg-green-50 ring-1 ring-green-300' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleStartRowClick(rowIdx)}>
                      <td className="px-2 py-1 text-gray-400 font-mono">{rowIdx + 1}</td>
                      {Array.from({ length: activePreview!.column_count }, (_, colIdx) => {
                        const isBoxCol = config.column_index === colIdx && !isBeforeStart
                        const isPalletCol = isDualColumnMode && config.column_index_pallets === colIdx && !isBeforeStart
                        return (
                          <td key={colIdx}
                            className={`px-2 py-1 font-mono ${
                              isBoxCol ? 'bg-blue-50 font-semibold text-blue-900' : isPalletCol ? 'bg-orange-50 font-semibold text-orange-900' : ''
                            }`}>
                            {row[colIdx] ?? ''}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!config.strict_checksum}
              onChange={(e) => setConfig(prev => ({ ...prev, strict_checksum: !e.target.checked }))}
              className="rounded text-blue-600" />
            <span>Допускать невалидный checksum (с предупреждением)</span>
          </label>

          <div className="bg-gray-50 rounded-md px-3 py-2 text-xs flex items-center gap-3 flex-wrap">
            <span>Начало: <strong>строка {config.start_row + 1}</strong></span>
            {isDualColumnMode ? (
              <>
                <span className="inline-flex items-center gap-1"><AppIcon name="box" className="h-3.5 w-3.5" /> Упаковки: <strong>Кол. {config.column_index + 1}</strong></span>
                <span className="inline-flex items-center gap-1"><AppIcon name="pallet" className="h-3.5 w-3.5" /> Паллеты: <strong>{config.column_index_pallets !== undefined ? `Кол. ${config.column_index_pallets + 1}` : '—'}</strong></span>
              </>
            ) : (
              <span>Колонка кодов: <strong>#{config.column_index + 1}</strong></span>
            )}
            <span>Кодов: <strong>{Math.max(0, activePreview!.total_lines - config.start_row)}</strong></span>
          </div>

          <div className="flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setStep('file')}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Назад
            </Button>
            <Button type="button" size="sm" onClick={handleConfirm} disabled={!canConfirm}>
              {confirmed ? <><CheckCircle className="w-4 h-4 mr-1" /> Подтверждено</> : <><Upload className="w-4 h-4 mr-1" /> Подтвердить выбор</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function StepBadge({ label, active, done, num }: { label: string; active: boolean; done: boolean; num: number }) {
  return (
    <div className={`flex items-center gap-1 ${active ? 'text-blue-600 font-medium' : done ? 'text-green-600' : 'text-gray-400'}`}>
      <span className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
        active ? 'bg-blue-100 text-blue-700' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
      }`}>
        {done ? <CheckCircle className="w-3 h-3" /> : num}
      </span>
      <span className="text-xs">{label}</span>
    </div>
  )
}

function FileDropZone({
  label, file, inputRef, onSelect,
}: {
  label: string
  file: File | null
  inputRef: React.RefObject<HTMLInputElement | null>
  onSelect: (e: ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
        onClick={() => inputRef.current?.click()}>
        <input ref={inputRef} type="file" accept=".txt,.csv,.xlsx,.xls" className="hidden" onChange={onSelect} />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-700">
            {file.name.match(/\.xlsx?$/i)
              ? <FileSpreadsheet className="w-4 h-4 text-green-600" />
              : <FileText className="w-4 h-4 text-blue-600" />}
            <span className="font-medium">{file.name}</span>
            <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        ) : (
          <div className="text-gray-400">
            <Upload className="w-6 h-6 mx-auto mb-1 text-gray-300" />
            <p className="text-xs">Нажмите для выбора файла</p>
            <p className="text-xs mt-0.5 text-gray-300">.txt, .csv, .xlsx</p>
          </div>
        )}
      </div>
    </div>
  )
}
